import React from "react";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import GetHeroIcon from "../../utils/getHeroIcon";
import Tooltip from "./tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import "./input.css";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  labelSize?: string;
  value?: any;
  error?: boolean;
  inputdisable?: boolean;
  errorMessage?: string;
  register?: (...args: any[]) => void;
  iconName?: string;
  iconPosition?: "leading" | "trailing" | undefined;
  iconVariant?: "outline" | "solid";
  optional?: boolean;
  infoicon?: boolean;
  setterFunction?: (value: any) => void;
  infoMessage?: string;
  maxLength?: number;
  errorIcon?: boolean;
  stepValue?: number;
  compactLabel?: boolean;
  hideDigitLimitError?: boolean;
  decimalAllowed?: boolean;
  allowNegativeOneOnly?: boolean;
}

const Input: React.FC<InputProps> = React.forwardRef(
  (
    {
      name,
      label,
      labelSize,
      value,
      register,
      error,
      errorMessage,
      iconName,
      iconPosition,
      iconVariant,
      optional,
      infoicon,
      infoMessage,
      inputdisable,
      setterFunction,
      maxLength,
      onChange,
      disabled,
      errorIcon = true,
      stepValue,
      compactLabel = false,
      hideDigitLimitError = false,
      decimalAllowed = false,
      allowNegativeOneOnly = false,
      ...rest
    },
    forwardedRef
  ): JSX.Element => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle(forwardedRef, () => inputRef.current);
    const handleKeyDown = (e: any) => {
      if (type === "number" && allowNegativeOneOnly) {
        const controlKeys = [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "Tab",
          "Home",
          "End",
          "Enter"
        ];
        if (controlKeys.includes(e.key)) {
          if (e.key === "Enter") e.preventDefault();
          return;
        }

        const isMinusKey =
          e.key === "-" || e.key === "Subtract" || e.code === "NumpadSubtract";
        if (isMinusKey) {
          const currentValue = String(e.currentTarget?.value ?? "");
          const selectionStart = e.currentTarget?.selectionStart ?? 0;
          const selectionEnd = e.currentTarget?.selectionEnd ?? 0;
          const replacingAll =
            selectionStart === 0 && selectionEnd === currentValue.length;
          const canInsertLeadingMinus =
            currentValue === "" || replacingAll || selectionStart === 0;
          if (!canInsertLeadingMinus || currentValue.includes("-")) {
            e.preventDefault();
          }
          return;
        }

        if (!/^\d$/.test(e.key)) {
          e.preventDefault();
          return;
        }
      }
      if (e.key == "Enter") {
        e.preventDefault();
      }
    };
    const [digitLimitError, setDigitLimitError] = React.useState(false);
    const { type, max } = rest;
    const effectiveType =
      allowNegativeOneOnly && type === "number" ? "text" : type;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // When the user types an incomplete decimal like "1.", browsers set
      // validity.badInput=true and expose value as "". Bail out early so we
      // don't overwrite the input and swallow the keystroke.
      if (
        effectiveType === "number" &&
        e.target.validity?.badInput &&
        !allowNegativeOneOnly
      )
        return;

      let value = e.target.value;

      if (
        type === "number" ||
        (allowNegativeOneOnly && effectiveType === "text")
      ) {
        if (allowNegativeOneOnly) {
          // Only allow a single leading "-" and digits.
          value = value.replace(/[^0-9-]/g, "");
          value = value.replace(/(?!^)-/g, "");
          if (value.startsWith("-") && value !== "-" && value !== "-1") {
            value = "-1";
          }
        } else if (decimalAllowed) {
          // Allow digits and a single decimal point
          value = value.replace(/[^0-9.]/g, "");
          const dotIndex = value.indexOf(".");
          if (dotIndex !== -1) {
            value =
              value.slice(0, dotIndex + 1) +
              value.slice(dotIndex + 1).replace(/\./g, "");
          }

          // Handle leading zeros, but preserve "0.x"
          if (value.length > 1 && value.startsWith("0") && value[1] !== ".") {
            value = value.replace(/^0+/, "");
            if (value === "" || value.startsWith(".")) {
              value = "0" + value;
            }
          }
        } else {
          // Integers only — strip everything except digits
          value = value.replace(/[^0-9]/g, "");

          // Handle leading zeros
          if (value.length > 1 && value.startsWith("0")) {
            value = value.replace(/^0+/, "");
          }
        }

        if (max !== undefined) {
          const intPartLen = value.split(".")[0].length;
          if (intPartLen > ("" + max).length) {
            setDigitLimitError(true);
            return;
          } else {
            setDigitLimitError(false);
          }
        }

        // The input 'step' attribute natively handles increment/decrement.
        // We do not forcefully round on change because it breaks manual typing.
        if (max !== undefined && value !== "-" && Number(value) > Number(max)) {
          value = max.toString();
        }

        // Only reassign when our sanitization actually changed the value.
        // If it's the same (e.g. user typed "1.3" and we kept "1.3") leave the
        // DOM input alone, otherwise we'd eat the trailing decimal point.
        if (e.target.value !== value) {
          e.target.value = value;
        }
      } else {
        // For non-number types, maintain previous harmless replacement
        value = value.replace(/\D/g, "");
      }

      if (setterFunction) {
        setterFunction(e);
      }
      onChange?.(e);
    };

    return (
      <div
        className="flex flex-col items-start space-y-1 w-full"
        role="presentation"
      >
        {label && (
          <label
            className={`text-secondary mb-1 font-medium ${compactLabel ? "inline-flex items-center" : "flex"} ${labelSize}`}
          >
            {label}
            {optional && (
              <span className="text-sm ps-2 font-thin">(Optional)</span>
            )}
            {infoicon && (
              <Tooltip
                message={` ${infoMessage}`}
                position="left-3  w-[10rem] -mt-3 ms-2"
              >
                {" "}
                <InformationCircleIcon className="w-6 h-6 ps-1 mt-0" />{" "}
              </Tooltip>
            )}
          </label>
        )}
        <div className="relative rounded-md shadow-sm w-full">
          {iconName && iconPosition === "leading" && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <GetHeroIcon
                name={iconName}
                className="w-5 h-5 text-secondary/50"
                aria-hidden="true"
                variant={iconVariant}
              />
            </div>
          )}
          <input
            maxLength={maxLength}
            disabled={inputdisable}
            value={value}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            className={`dark:placeholder-gray-400 dark:bg-bkg dark:ring-bkg block w-full rounded-md border-0 dark:ring-secondary/20 py-1.5 focus:ring-inset ${
              iconName && iconPosition === "leading"
                ? "pl-10 pr-3"
                : iconPosition === "trailing"
                  ? "pr-10 pl-3"
                  : "px-3"
            } disabled:dark:text-gray-300 text-secondary shadow-sm ring-1 ring-inset ${
              error
                ? "ring-red-500 focus:ring-red-500"
                : "ring-border focus:ring-primary"
            } placeholder:text-secondary/50 sm:text-sm sm:leading-6 outline-none disabled:cursor-not-allowed disabled:bg-gray-300  disabled:text-gray-500 dark:text-gray-300  disabled:ring-gray-0
            dark:disabled:bg-gray-800`}
            {...(register ? register(name) : {})}
            {...rest}
            type={effectiveType}
            inputMode={allowNegativeOneOnly ? "numeric" : undefined}
            step={effectiveType === "number" ? stepValue ?? 1 : undefined}
            onInput={handleChange}
          />
          {iconName && iconPosition === "trailing" && !error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <GetHeroIcon
                name={iconName}
                className="w-5 h-5  text-secondary/50"
                aria-hidden="true"
                variant={iconVariant}
              />
            </div>
          )}
          {error && errorIcon && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-7">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
        {error && errorMessage && (
          <p className="text-sm text-red-500">{errorMessage}</p>
        )}
        {digitLimitError && !hideDigitLimitError && (
          <p className="text-xs text-red-500 mt-1">
            Value must not exceed {max}
          </p>
        )}
      </div>
    );
  }
);

export default Input;
